/* eslint-disable max-lines -- cannot separate */
import { D, G } from '@mobily/ts-belt';
import InfoIcon from '@mui/icons-material/Info';
import {
  Box,
  Card,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useTranslate } from 'react-admin';
import { match } from 'ts-pattern';

import { FINANCIAL_INDEX_SHORT_NAME } from '@Common/constants/financial_indexes';
import {
  MatchingContributionConfig,
  MatchingContributionConfigLimitBrackets,
} from '@Common/types/bo/b2b/matching_contribution_config';
import { B2bCompanyPlan, getMaxPassLimit } from '@Common/types/bo/b2b/plans';

import {
  cashSourcesChoices,
  seniorityBrackets,
} from '@sh4res/bo-common/choices/matching_contribution';
import { ShowFields, ShowSection } from '@sh4res/bo-common/components';
import { CurrencyField, SelectChipFieldsList } from '@sh4res/bo-common/fields';
import { useFinancialIndexValue } from '@sh4res/bo-common/hooks';

const cellBoxStyle = {
  display: 'grid',
  gridTemplateColumns: 'auto auto',
  gridColumnGap: '10px',
};

const computeMaxMatchingContribution = (
  planType: B2bCompanyPlan['category'],
  brackets: MatchingContributionConfigLimitBrackets['brackets'],
  passCeilingCents: number | null,
) => {
  if (G.isNullable(passCeilingCents)) {
    return brackets.map((bracket) => ({ ...bracket, maxContribution: undefined }));
  }

  let acc = 0;
  const maxPassLimit = getMaxPassLimit(planType, passCeilingCents);
  return brackets.map((bracket, idx) => {
    const prevBracket = brackets[idx - 1] ?? {
      upper_limit: { type: 'absolute_amount', amount: 0 },
    };
    const maxContribution = match(bracket.upper_limit)
      .with({ type: 'percentage_of_legal_cap' }, (limit) => {
        if (prevBracket.upper_limit.type !== 'absolute_amount') {
          return 0;
        }
        const amount = Math.min(
          (((limit.percentage / 100) * passCeilingCents - prevBracket.upper_limit.amount) *
            bracket.contribution_pct) /
            100,
          maxPassLimit - acc,
        );
        acc += amount;
        return amount;
      })
      .with({ type: 'absolute_amount' }, (limit) => {
        if (prevBracket.upper_limit.type !== 'absolute_amount') {
          return 0;
        }
        const amount = Math.min(
          ((limit.amount - prevBracket.upper_limit.amount) * bracket.contribution_pct) / 100,
          maxPassLimit - acc,
        );
        acc += amount;
        return amount;
      })
      .exhaustive();
    return {
      ...bracket,
      maxContribution,
    };
  });
};

const testId = 'BoExternalB2bPlansMatchingContributionSettings';

// eslint-disable-next-line max-lines-per-function -- All MC rules in one file, but needs optimization
export const MatchingContributionSettings = ({ plan }: { plan: Omit<B2bCompanyPlan, 'fees'> }) => {
  const translate = useTranslate();
  const passCeilingCents = useFinancialIndexValue(FINANCIAL_INDEX_SHORT_NAME.PASS);

  if (!plan.matching_contribution_config) {
    return null;
  }
  return match(plan.matching_contribution_config)
    .with({ type: 'simple' }, (config) => (
      <Card sx={{ margin: '15px' }}>
        <Table>
          <TableHead>
            <TableRow sx={{ fontWeight: 'bold', height: '50px' }}>
              <TableCell>
                {translate(
                  'resources.bo_common.b2b.companies.saving_plans.sections.matching_contributions.simple.source',
                )}
              </TableCell>
              <TableCell>
                {translate(
                  'resources.bo_common.b2b.companies.saving_plans.sections.matching_contributions.simple.rate',
                )}
              </TableCell>
              <TableCell>
                {translate(
                  'resources.bo_common.b2b.companies.saving_plans.sections.matching_contributions.simple.limit',
                )}
              </TableCell>
              <TableCell>
                {translate(
                  'resources.bo_common.b2b.companies.saving_plans.sections.matching_contributions.simple.limit_pct',
                )}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {Object.keys(config.limits_per_contribution_type).map((mode) => {
              const data = config.limits_per_contribution_type[mode];
              if (!data) {
                return null;
              }
              return (
                <TableRow key={mode} sx={{ height: '50px' }}>
                  <TableCell>
                    {translate(
                      `resources.bo_common.b2b.companies.saving_plans.cash_sources.${mode}`,
                    )}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ ...cellBoxStyle, width: 'fit-content' }}>
                      {data.contribution_pct}%
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ ...cellBoxStyle, width: 'fit-content' }}>
                      {data.limit.type === 'absolute_amount' ? (
                        <CurrencyField
                          source="amount"
                          record={{ amount: data.limit.amount }}
                          currency="EUR"
                          testId={`${testId}_SimpleLimitAmount`}
                        />
                      ) : (
                        '-'
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ ...cellBoxStyle, width: 'fit-content' }}>
                      {data.limit.type === 'percentage_of_legal_cap'
                        ? `${data.limit.percentage}%`
                        : '-'}
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Card>
    ))
    .with({ type: 'csp_code' }, (config) => (
      <>
        <ShowFields<MatchingContributionConfig>
          resource="bo_common.b2b.companies.saving_plans"
          fields={{
            supported_contribution_types: (
              <SelectChipFieldsList record={config} choices={cashSourcesChoices} />
            ),
          }}
        />
        <Card sx={{ margin: '15px' }}>
          <Table>
            <TableHead>
              <TableRow sx={{ fontWeight: 'bold', height: '50px' }}>
                <TableCell>
                  {translate(
                    'resources.bo_common.b2b.companies.saving_plans.sections.matching_contributions.csp_code.value',
                  )}
                </TableCell>
                <TableCell>
                  {translate(
                    'resources.bo_common.b2b.companies.saving_plans.sections.matching_contributions.simple.rate',
                  )}
                </TableCell>
                <TableCell>
                  {translate(
                    'resources.bo_common.b2b.companies.saving_plans.sections.matching_contributions.simple.limit',
                  )}
                </TableCell>
                <TableCell>
                  {translate(
                    'resources.bo_common.b2b.companies.saving_plans.sections.matching_contributions.simple.limit_pct',
                  )}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {Object.entries(config.limits_per_csp_code).map(([code, data]) => (
                <TableRow key={code} sx={{ height: '50px' }}>
                  <TableCell>
                    {code === ''
                      ? translate(
                          'resources.bo_common.b2b.companies.saving_plans.sections.matching_contributions.csp_code.no_code',
                        )
                      : code}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ ...cellBoxStyle, width: 'fit-content' }}>
                      {data.contribution_pct}%
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ ...cellBoxStyle, width: 'fit-content' }}>
                      {data.limit.type === 'absolute_amount' ? (
                        <CurrencyField
                          source="amount"
                          record={{ amount: data.limit.amount }}
                          currency="EUR"
                          testId={`${testId}_CspCodeLimitAmount`}
                        />
                      ) : (
                        '-'
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ ...cellBoxStyle, width: 'fit-content' }}>
                      {data.limit.type === 'percentage_of_legal_cap'
                        ? `${data.limit.percentage}%`
                        : '-'}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      </>
    ))
    .with({ type: 'csp_code_per_cash_source' }, (config) =>
      config.supported_contribution_types.map((cashSourceConfig) => (
        <ShowSection
          sx={{ margin: '15px' }}
          leftAligned
          key={cashSourceConfig.cash_source}
          label="resources.bo_common.b2b.companies.saving_plans.sections.matching_contributions.rule_settings_per_cash_source"
          labelParams={{
            cash_source: translate(
              `resources.bo_common.b2b.companies.saving_plans.cash_sources.${cashSourceConfig.cash_source}`,
            ),
          }}
          noCard>
          <Card>
            <Table>
              <TableHead>
                <TableRow sx={{ fontWeight: 'bold', height: '50px' }}>
                  <TableCell>
                    {translate(
                      'resources.bo_common.b2b.companies.saving_plans.sections.matching_contributions.csp_code.value',
                    )}
                  </TableCell>
                  <TableCell>
                    {translate(
                      'resources.bo_common.b2b.companies.saving_plans.sections.matching_contributions.simple.rate',
                    )}
                  </TableCell>
                  <TableCell>
                    {translate(
                      'resources.bo_common.b2b.companies.saving_plans.sections.matching_contributions.simple.limit',
                    )}
                  </TableCell>
                  <TableCell>
                    {translate(
                      'resources.bo_common.b2b.companies.saving_plans.sections.matching_contributions.simple.limit_pct',
                    )}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {Object.entries(cashSourceConfig.limits_per_csp_code).map(([code, data]) => (
                  <TableRow key={code} sx={{ height: '50px' }}>
                    <TableCell>
                      {code === ''
                        ? translate(
                            'resources.bo_common.b2b.companies.saving_plans.sections.matching_contributions.csp_code.no_code',
                          )
                        : code}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ ...cellBoxStyle, width: 'fit-content' }}>
                        {data.contribution_pct}%
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ ...cellBoxStyle, width: 'fit-content' }}>
                        {data.limit.type === 'absolute_amount' ? (
                          <CurrencyField
                            source="amount"
                            record={{ amount: data.limit.amount }}
                            currency="EUR"
                            testId={`${testId}_CspCodePerCashSourceLimit`}
                          />
                        ) : (
                          '-'
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ ...cellBoxStyle, width: 'fit-content' }}>
                        {data.limit.type === 'percentage_of_legal_cap'
                          ? `${data.limit.percentage}%`
                          : '-'}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </ShowSection>
      )),
    )
    .with({ type: 'limit_brackets' }, (config) => (
      <>
        <ShowFields<MatchingContributionConfig>
          resource="bo_common.b2b.companies.saving_plans"
          fields={{
            supported_contribution_types: (
              <SelectChipFieldsList record={config} choices={cashSourcesChoices} />
            ),
          }}
        />
        <Card sx={{ margin: '15px' }}>
          <Table>
            <TableHead>
              <TableRow sx={{ fontWeight: 'bold', height: '50px' }}>
                <TableCell>
                  {translate(
                    'resources.bo_common.b2b.companies.saving_plans.sections.matching_contributions.limit_brackets.rate',
                  )}
                </TableCell>
                <TableCell>
                  {translate(
                    'resources.bo_common.b2b.companies.saving_plans.sections.matching_contributions.limit_brackets.to',
                  )}
                </TableCell>
                <TableCell
                  sx={{
                    flexDirection: 'row',
                  }}>
                  <InfoIcon sx={{ color: 'grey', marginRight: '5px', verticalAlign: 'middle' }} />
                  {translate(
                    'resources.bo_common.b2b.companies.saving_plans.sections.matching_contributions.limit_brackets.max',
                  )}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {computeMaxMatchingContribution(plan.category, config.brackets, passCeilingCents).map(
                (bracket, idx) => (
                  <TableRow key={idx} sx={{ height: '50px' }}>
                    <TableCell>
                      <Box sx={{ ...cellBoxStyle, width: 'fit-content' }}>
                        {bracket.contribution_pct}%
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ ...cellBoxStyle, width: 'fit-content' }}>
                        {bracket.upper_limit.type === 'percentage_of_legal_cap' ? (
                          `${bracket.upper_limit.percentage}% PASS`
                        ) : (
                          <CurrencyField
                            source="upper_limit.amount"
                            currency="EUR"
                            record={bracket}
                            testId={`${testId}_LimitBracketUpperAmount`}
                          />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography sx={{ color: 'grey' }}>
                        <CurrencyField
                          source="amount"
                          currency="EUR"
                          precision={2}
                          record={{ amount: bracket.maxContribution }}
                          testId={`${testId}_LimitBracketMaxAmount`}
                        />
                        max.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ),
              )}
            </TableBody>
          </Table>
        </Card>
      </>
    ))
    .with({ type: 'seniority_brackets' }, (config) => (
      <>
        <ShowFields<MatchingContributionConfig & Record<'rate', unknown>>
          resource="bo_common.b2b.companies.saving_plans"
          fields={{
            supported_contribution_types: (
              <SelectChipFieldsList record={config} choices={cashSourcesChoices} />
            ),
            rate: <Typography>{config.contribution_pct}%</Typography>,
          }}
        />
        <Card sx={{ margin: '15px' }}>
          <Table>
            <TableHead>
              <TableRow sx={{ fontWeight: 'bold', height: '50px' }}>
                <TableCell>
                  {translate(
                    'resources.bo_common.b2b.companies.saving_plans.sections.matching_contributions.seniority_brackets.tenure',
                  )}
                </TableCell>
                {config.brackets[0]?.upper_limit.type === 'percentage_of_legal_cap' ? (
                  <TableCell>
                    {translate(
                      'resources.bo_common.b2b.companies.saving_plans.sections.matching_contributions.seniority_brackets.limit_pct',
                    )}
                  </TableCell>
                ) : (
                  <TableCell>
                    {translate(
                      'resources.bo_common.b2b.companies.saving_plans.sections.matching_contributions.seniority_brackets.limit',
                    )}
                  </TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {config.brackets.map((bracket, idx) => (
                <TableRow key={idx} sx={{ height: '50px' }}>
                  <TableCell>
                    {seniorityBrackets.find(
                      (info) =>
                        info.years_in_company_max_exclusive ===
                          bracket.years_in_company_max_exclusive &&
                        info.years_in_company_min_inclusive ===
                          bracket.years_in_company_min_inclusive,
                    )?.label ?? '-'}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ ...cellBoxStyle, width: 'fit-content' }}>
                      {bracket.upper_limit.type === 'percentage_of_legal_cap' ? (
                        `${bracket.upper_limit.percentage}% PASS`
                      ) : (
                        <CurrencyField
                          source="upper_limit.amount"
                          currency="EUR"
                          record={bracket}
                          testId={`${testId}_SeniorityBracketUpperAmount`}
                        />
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      </>
    ))
    .with({ type: 'company_simple' }, (config) => (
      <Card sx={{ margin: '15px' }}>
        <Table>
          <TableHead>
            <TableRow sx={{ fontWeight: 'bold', height: '50px' }}>
              <TableCell>
                {translate(
                  'resources.bo_common.b2b.companies.saving_plans.sections.matching_contributions.simple.source',
                )}
              </TableCell>
              <TableCell>
                {translate(
                  'resources.bo_common.b2b.companies.saving_plans.sections.matching_contributions.simple.limit',
                )}
              </TableCell>
              <TableCell>
                {translate(
                  'resources.bo_common.b2b.companies.saving_plans.sections.matching_contributions.simple.limit_pct',
                )}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {D.keys(config.limits_per_contribution_type).map((mode) => {
              const data = config.limits_per_contribution_type[mode];
              if (!data) {
                return null;
              }
              return (
                <TableRow key={mode} sx={{ height: '50px' }}>
                  <TableCell>
                    {translate(
                      `resources.bo_common.b2b.companies.saving_plans.cash_sources.${mode}`,
                    )}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ ...cellBoxStyle, width: 'fit-content' }}>
                      {data.limit.type === 'absolute_amount' ? (
                        <CurrencyField
                          source="amount"
                          record={{ amount: data.limit.amount }}
                          currency="EUR"
                          testId={`${testId}_CompanySimpleLimitAmount`}
                        />
                      ) : (
                        '-'
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ ...cellBoxStyle, width: 'fit-content' }}>
                      {data.limit.type === 'percentage_of_legal_cap'
                        ? `${data.limit.percentage}%`
                        : '-'}
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Card>
    ))
    .with({ type: 'limit_brackets_per_cash_source' }, (config) =>
      config.supported_contribution_types.map((cashSource) => (
        <ShowSection
          sx={{ margin: '15px' }}
          leftAligned
          key={cashSource.cash_source}
          label="resources.bo_common.b2b.companies.saving_plans.sections.matching_contributions.rule_settings_per_cash_source"
          labelParams={{
            cash_source: translate(
              `resources.bo_common.b2b.companies.saving_plans.cash_sources.${cashSource.cash_source}`,
            ),
          }}
          noCard>
          <Card>
            <Table>
              <TableHead>
                <TableRow sx={{ fontWeight: 'bold', height: '50px' }}>
                  <TableCell>
                    {translate(
                      'resources.bo_common.b2b.companies.saving_plans.sections.matching_contributions.limit_brackets.rate',
                    )}
                  </TableCell>
                  <TableCell>
                    {translate(
                      'resources.bo_common.b2b.companies.saving_plans.sections.matching_contributions.limit_brackets.to',
                    )}
                  </TableCell>
                  <TableCell
                    sx={{
                      flexDirection: 'row',
                    }}>
                    <InfoIcon sx={{ color: 'grey', marginRight: '5px', verticalAlign: 'middle' }} />
                    {translate(
                      'resources.bo_common.b2b.companies.saving_plans.sections.matching_contributions.limit_brackets.max',
                    )}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {computeMaxMatchingContribution(
                  plan.category,
                  cashSource.brackets,
                  passCeilingCents,
                ).map((bracket, idx) => (
                  <TableRow key={idx} sx={{ height: '50px' }}>
                    <TableCell>
                      <Box sx={{ ...cellBoxStyle, width: 'fit-content' }}>
                        {bracket.contribution_pct}%
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ ...cellBoxStyle, width: 'fit-content' }}>
                        {bracket.upper_limit.type === 'percentage_of_legal_cap' ? (
                          `${bracket.upper_limit.percentage}% PASS`
                        ) : (
                          <CurrencyField
                            source="upper_limit.amount"
                            currency="EUR"
                            record={bracket}
                            testId={`${testId}_PerCashSourceUpperAmount`}
                          />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography sx={{ color: 'grey' }}>
                        <CurrencyField
                          source="amount"
                          currency="EUR"
                          precision={2}
                          record={{ amount: bracket.maxContribution }}
                          testId={`${testId}_PerCashSourceMaxAmount`}
                        />
                        max.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </ShowSection>
      )),
    )
    .with({ type: 'csp_code_per_cash_source_brackets' }, (_config) => (
      <Card sx={{ margin: '15px' }}>
        <Typography variant="body1">
          {translate(
            'resources.bo_common.b2b.companies.saving_plans.sections.matching_contributions.no_display_available',
          )}
        </Typography>
      </Card>
    ))
    .exhaustive();
};
